// EmployeeManagementPage.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  FaDownload, 
  FaUsers, 
  FaChevronLeft, 
  FaChevronRight, 
  FaUserPlus, 
  FaUserEdit, 
  FaExchangeAlt, 
  FaFolderOpen, 
  FaProjectDiagram, 
  FaSearch,
  FaFilter,
  FaTimes,
  FaUserCog,
  FaBell,
  FaIdCard
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, query, where, deleteField, deleteDoc, orderBy, limit, startAfter, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './EmployeeManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import './EmployeeManagementDarkMode.css'; // Import dark mode styles

// Imported modal components
import EditEmployeeModal from './EditEmployeeModal';
import FileUploadModal from './FileUploadModal';
import ProjectsModal from './ProjectsModal';
import AddEmployeeModal from './AddEmployeeModal';
import AddInsuranceModal from './AddInsuranceModal';
import EditInsuranceModal from './EditInsuranceModal';
import TableFilter from '../../components/TableFilter';
import CreateUserModal from './CreateUserModal';
import ExitDateModal from './ExitDateModal';
import EmployeeCardModal from './EmployeeCardModal';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

// Tarih alanlarını güvenli şekilde biçimlendirmek için yardımcı fonksiyon
const formatDate = (value) => {
  if (!value) return '-';
  // Firestore tarihleri string (YYYY-MM-DD) veya Timestamp/ISO olabilir
  try {
    const date = new Date(value);
    if (isNaN(date)) return truncateText(value);
    return date.toLocaleDateString('tr-TR');
  } catch (e) {
    return truncateText(value);
  }
};

const EmployeeManagementPage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const { userAuthorization } = useUserProfile();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showEditInsuranceModal, setShowEditInsuranceModal] = useState(false);
  const [selectedInsuranceData, setSelectedInsuranceData] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [filters, setFilters] = useState({ Active: '1' });
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allEmployeesForFilter, setAllEmployeesForFilter] = useState([]);
  const [statsData, setStatsData] = useState({ active: 0, inactive: 0 });
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [selectedEmployeeHasUser, setSelectedEmployeeHasUser] = useState(false);
  const [isCheckingUser, setIsCheckingUser] = useState(false);
  const [showExitDateModal, setShowExitDateModal] = useState(false);
  const [pendingLeaveCounts, setPendingLeaveCounts] = useState({});
  const [showPendingLeavesModal, setShowPendingLeavesModal] = useState(false);
  const [pendingLeavesList, setPendingLeavesList] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(40); // Fetch 40 employees per page
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [firstVisible, setFirstVisible] = useState(null);
  const [pageCache, setPageCache] = useState(() => {
    // Initialize from localStorage if available
    try {
      const cached = localStorage.getItem('employeesPageCache');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Error loading cache:', e);
      return {};
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState({
    eName: true,
    eSurname: true,
    tcNumber: true,
    phone: true,
    departman: true,
    Active: true,
    address: false,
    workAddress: false,
    annualPermit: false,
    birthday: false,
    eMail: false,
    isHandicappedPerson: false,
    isIntern: false,
    isRetired: false,
    languageInfo: false,
    nationality: false,
    profession: false,
    educationInfo: false,
    startDate: false,
    employeeExitDate: true,
    workType: false
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    eName: { label: 'Ad', type: 'text' },
    eSurname: { label: 'Soyad', type: 'text' },
    tcNumber: { label: 'TC No', type: 'text' },
    phone: { label: 'Telefon', type: 'text' },
    departman: { label: 'Departman', type: 'text' },
    Active: {
      label: 'Durum',
      type: 'select',
      options: [
        { value: 1, label: 'Aktif' },
        { value: 0, label: 'İnaktif' }
      ]
    },
    address: { label: 'Ev Adresi', type: 'text' },
    workAddress: { label: 'İş Adresi', type: 'text' },
    annualPermit: { label: 'Yıllık İzin', type: 'text' },
    birthday: { label: 'Doğum Tarihi', type: 'date' },
    eMail: { label: 'E-posta', type: 'text' },
    isHandicappedPerson: {
      label: 'Engellilik Durumu',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    isIntern: {
      label: 'Stajyer',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    isRetired: {
      label: 'Emekli',
      type: 'select',
      options: [
        { value: 'Yes', label: 'Evet' },
        { value: 'No', label: 'Hayır' }
      ]
    },
    languageInfo: { label: 'Dil Bilgisi', type: 'text' },
    nationality: { label: 'Uyruk', type: 'text' },
    profession: { label: 'Meslek', type: 'text' },
    educationInfo: {
      label: 'Eğitim Bilgisi',
      type: 'select',
      options: [
        { value: 'Ortaokul / Ortaöğretim', label: 'Ortaokul / Ortaöğretim' },
        { value: 'Lise', label: 'Lise' },
        { value: 'Ön Lisans (2 Yıllık Üniversite)', label: 'Ön Lisans (2 Yıllık Üniversite)' },
        { value: 'Lisans (4 Yıllık Üniversite)', label: 'Lisans (4 Yıllık Üniversite)' },
        { value: 'Yüksek Lisans (Master)', label: 'Yüksek Lisans (Master)' },
        { value: 'Doktora (PhD)', label: 'Doktora (PhD)' }
      ]
    },
    startDate: { label: 'Başlangıç Tarihi', type: 'date' },
    employeeExitDate: { label: 'İşten Ayrılma Tarihi', type: 'date' },
    workType: {
      label: 'Çalışma Şekli',
      type: 'select',
      options: [
        { value: 'Full Time', label: 'Tam Zamanlı' },
        { value: 'Part Time', label: 'Yarı Zamanlı' }
      ]
    }
  };

  // Fetch all employees for filtering/searching
  const fetchAllEmployeesForFilter = async () => {
    try {
      const allEmployeesQuery = query(collection(db, 'Employees'), orderBy('eName'));
      const querySnapshot = await getDocs(allEmployeesQuery);
      const allEmployees = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllEmployeesForFilter(allEmployees);
      setTotalEmployees(allEmployees.length);
      return allEmployees;
    } catch (error) {
      console.error('Error fetching all employees:', error);
      return [];
    }
  };

  // Fetch total employee count and stats
  const fetchTotalCount = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const total = querySnapshot.size;
      setTotalEmployees(total);

      // Calculate stats from the fetched data
      const allDocs = querySnapshot.docs.map(doc => doc.data());
      const active = allDocs.filter(emp => emp.Active === 1).length;
      const inactive = allDocs.filter(emp => emp.Active !== 1).length;
      setStatsData({ active, inactive });
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  // Fetch employees with pagination
  const fetchEmployees = async (page = 1, useCache = true) => {
    // Check cache first
    if (useCache && pageCache[page]) {
      setEmployees(pageCache[page]);
      setCurrentPage(page);
      return;
    }

    setIsLoading(true);
    try {
      let employeesQuery;

      if (page === 1) {
        // First page query
        employeesQuery = query(
          collection(db, 'Employees'),
          orderBy('eName'),
          limit(pageSize)
        );
      } else if (page > currentPage && lastVisible) {
        // Next page query
        employeesQuery = query(
          collection(db, 'Employees'),
          orderBy('eName'),
          startAfter(lastVisible),
          limit(pageSize)
        );
      } else if (page < currentPage && firstVisible) {
        // Previous page query - this is tricky, we'll use cache or refetch from page 1
        if (pageCache[page]) {
          setEmployees(pageCache[page]);
          setCurrentPage(page);
          setIsLoading(false);
          return;
        }
        // Fallback: refetch from beginning
        employeesQuery = query(
          collection(db, 'Employees'),
          orderBy('eName'),
          limit(pageSize * page)
        );
      } else {
        // Default to first page
        employeesQuery = query(
          collection(db, 'Employees'),
          orderBy('eName'),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(employeesQuery);

      let employeesData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      // If fetching multiple pages for previous page navigation
      if (page < currentPage && page > 1) {
        // Get only the last pageSize items
        employeesData = employeesData.slice(-pageSize);
      }

      // Store first and last visible documents for pagination
      if (querySnapshot.docs.length > 0) {
        setFirstVisible(querySnapshot.docs[0]);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }

      setEmployees(employeesData);
      setCurrentPage(page);

      // Cache the page in state and localStorage
      setPageCache(prev => {
        const newCache = {
          ...prev,
          [page]: employeesData
        };
        try {
          localStorage.setItem('employeesPageCache', JSON.stringify(newCache));
        } catch (e) {
          console.error('Error saving cache:', e);
        }
        return newCache;
      });

    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışanlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear cache when data changes
  const refreshEmployees = () => {
    setPageCache({});
    try {
      localStorage.removeItem('employeesPageCache');
    } catch (e) {
      console.error('Error clearing cache:', e);
    }
    setCurrentPage(1);
    // Refresh both all employees (for filtering) and paginated employees
    fetchAllEmployeesForFilter().then(() => {
      fetchEmployees(1, false);
    });
  };

  useEffect(() => {
    // Only fetch first page on initial load (don't fetch all employees until needed)
    fetchEmployees(1, false);
    fetchTotalCount();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Employees yüklendiğinde bekleyen izin taleplerini say
  useEffect(() => {
    if (employees.length > 0) {
      fetchPendingLeaveCounts();
    }
  }, [employees]);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    // Check if any filters or search are active (with minimum 3 characters for search)
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      // Active filter with value '1' is default, so don't count it as active filter
      if (key === 'Active' && filters[key] === '1') return false;
      // For text filters, require at least 3 characters
      if (filters[key] && typeof filters[key] === 'string') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    const isAnyFilterActive = hasActiveSearch || hasActiveFilters;
    setIsFilterActive(isAnyFilterActive);

    // Fetch all employees if filter is active and we haven't fetched them yet
    if (isAnyFilterActive && allEmployeesForFilter.length === 0) {
      fetchAllEmployeesForFilter();
    }

    // Use all employees when filter/search is active, otherwise use paginated employees
    let sourceData = isAnyFilterActive ? allEmployeesForFilter : employees;
    let filtered = sourceData;

    // Apply search term first (searches name, surname, and TC number) - minimum 3 characters
    if (searchTerm && searchTerm.trim().length >= 3) {
      const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(
        emp =>
          (emp.eName && emp.eName.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (emp.eSurname && emp.eSurname.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (emp.tcNumber && emp.tcNumber.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Active') {
          // Active filter always applies (no minimum length)
          filtered = filtered.filter(item =>
            String(item[key]) === String(filters[key])
          );
        } else {
          // Text filters require minimum 3 characters
          const filterValue = String(filters[key]);
          if (filterValue.trim().length >= 3) {
            filtered = filtered.filter(item =>
              String(item[key])
                ?.toLocaleLowerCase('tr-TR')
                .includes(filterValue.toLocaleLowerCase('tr-TR'))
            );
          }
        }
      }
    });

    // Sort employees alphabetically by name and surname
    filtered.sort((a, b) => {
      const nameA = `${a.eName || ''} ${a.eSurname || ''}`.trim().toLocaleLowerCase('tr-TR');
      const nameB = `${b.eName || ''} ${b.eSurname || ''}`.trim().toLocaleLowerCase('tr-TR');
      return nameA.localeCompare(nameB, 'tr');
    });

    setFilteredEmployees(filtered);
  }, [employees, allEmployeesForFilter, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Active filtresi 1 iken employeeExitDate sütununu otomatik gizle
  const effectiveVisibleColumns = useMemo(() => {
    const clone = { ...visibleColumns };
    if (String(filters.Active) === '1') {
      clone.employeeExitDate = false;
    }
    return clone;
  }, [visibleColumns, filters.Active]);

  // Use stats data fetched separately for accurate counts
  const activeCount = statsData.active;
  const inactiveCount = statsData.inactive;
  const totalPendingLeaves = pendingLeavesList.length;
  const totalPages = Math.ceil(totalEmployees / pageSize);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      fetchEmployees(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      fetchEmployees(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchEmployees(page);
    }
  };

  const downloadExcel = async () => {
    try {
      // Fetch all employees for export
      toast.info('Excel dosyası hazırlanıyor...');
      const allEmployeesQuery = query(collection(db, 'Employees'), orderBy('eName'));
      const allEmployeesSnapshot = await getDocs(allEmployeesQuery);
      const allEmployees = allEmployeesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Employees');
      worksheet.columns = [
        { header: 'First Name', key: 'eName', width: 15 },
        { header: 'Last Name', key: 'eSurname', width: 15 },
        { header: 'TC No', key: 'tcNumber', width: 15 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Department', key: 'departman', width: 20 },
        { header: 'Exit Date', key: 'employeeExitDate', width: 14 },
        { header: 'Status', key: 'Active', width: 10 }
      ];
      allEmployees.forEach(emp => {
        worksheet.addRow({
          eName: truncateText(emp.eName),
          eSurname: truncateText(emp.eSurname),
          tcNumber: truncateText(emp.tcNumber),
          phone: truncateText(emp.phone),
          departman: truncateText(emp.departman),
          employeeExitDate: formatDate(emp.employeeExitDate),
          Active: emp.Active === 1 ? 'Active' : 'Inactive'
        });
      });
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Error downloading file');
    }
  };

  // Çalışanı tüm projelerden aktif/pasif duruma geçir
  const updateEmployeeStatusInProjects = async (employeeId, targetStatus) => {
    try {
      // Çalışanın mevcut proje atamalarını al
      const employeeRef = doc(db, 'Employees', employeeId);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) return;
      
      const employeeData = employeeDoc.data();
      const projects = employeeData.projects || [];
      
      // Çalışanın atandığı tüm projeleri güncelle
      const updatePromises = [];
      
      // Benzersiz proje ID'lerini bul
      const uniqueProjectIds = [...new Set(projects.map(p => p.id))];
      
      for (const projectId of uniqueProjectIds) {
        const projectRef = doc(db, 'Projects', projectId);
        const projectDoc = await getDoc(projectRef);
        
        if (projectDoc.exists()) {
          const projectData = projectDoc.data();
          const projectEmployees = projectData.employees || [];
          
          // Bu çalışanın tüm girişlerini hedef duruma geçir
          const updatedEmployees = projectEmployees.map(emp => {
            if (emp.id === employeeId) {
              return {
                ...emp,
                status: targetStatus
              };
            }
            return emp;
          });
          
          // Eğer değişiklik varsa güncelle
          const hasChanges = projectEmployees.some(emp => emp.id === employeeId && emp.status !== targetStatus);
          if (hasChanges) {
            updatePromises.push(
              updateDoc(projectRef, { employees: updatedEmployees })
            );
          }
        }
      }
      
      // Çalışanın kendi projects dizisindeki durumları da güncelle
      const updatedProjects = projects.map(proj => ({
        ...proj,
        status: targetStatus
      }));
      
      updatePromises.push(
        updateDoc(employeeRef, { projects: updatedProjects })
      );
      
      await Promise.all(updatePromises);
      
      console.log(`Çalışan ${employeeId} tüm projelerden ${targetStatus} duruma geçirildi`);
    } catch (error) {
      console.error('Error updating employee status in projects:', error);
      throw error;
    }
  };

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  const handleStatusToggle = async () => {
    if (!selectedEmployee) return;
    const newStatus = selectedEmployee.Active === 1 ? 0 : 1;
    
    // Eğer çalışan inaktif yapılıyorsa, işten ayrılma tarihi modalını aç
    if (newStatus === 0) {
      setShowExitDateModal(true);
      return;
    }
    
    // Eğer çalışan aktif yapılıyorsa, direkt güncelle ve projelerini de aktif yap
    try {
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);
      
      // Önce çalışanı tüm projelerden aktif yap
      await updateEmployeeStatusInProjects(selectedEmployee.id, 'active');
      
      // Sonra çalışanın durumunu güncelle
      await updateDoc(employeeRef, { Active: newStatus, employeeExitDate: deleteField() });
      
      // Dinamik olarak seçili çalışanın durumunu güncelle (tarih temizlensin)
      const updatedEmployee = { ...selectedEmployee, Active: newStatus, employeeExitDate: null };
      setSelectedEmployee(updatedEmployee);
      
      // Çalışanlar listesini de güncelle
      setEmployees(prevEmployees => 
        prevEmployees.map(emp => 
          emp.id === selectedEmployee.id 
            ? { ...emp, Active: newStatus, employeeExitDate: null }
            : emp
        )
      );
      
      toast.success(`Çalışan durumu ${newStatus === 1 ? 'Aktif' : 'İnaktif'} olarak güncellendi ve tüm projeleri ${newStatus === 1 ? 'aktif' : 'pasif'} duruma geçirildi`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken hata oluştu');
    }
  };

  const openEditModal = () => setShowEditModal(true);
  const openCardModal = () => setShowCardModal(true);
  const openFileModal = () => setShowFileModal(true);
  const openProjectsModal = () => setShowProjectsModal(true);
  const openAddModal = () => setShowAddModal(true);
  const openInsuranceModal = () => setShowInsuranceModal(true);
  const openEditInsuranceModal = (data) => {
    setSelectedInsuranceData(data);
    setShowEditInsuranceModal(true);
  };
  const openVideoModal = (videoUrl) => {
    setCurrentVideo(videoUrl);
    setShowVideoModal(true);
  };
  const openCreateUserModal = () => setShowCreateUserModal(true);

  // Bekleyen izin taleplerini say
  const fetchPendingLeaveCounts = async () => {
    try {
      const counts = {};
      
      // Her çalışan için bekleyen izin taleplerini say
      for (const employee of employees) {
        if (employee.id) {
          const q = query(
            collection(db, 'LeaveRequests'),
            where('employeeId', '==', employee.id),
            where('status', '==', 'PENDING')
          );
          const querySnapshot = await getDocs(q);
          counts[employee.id] = querySnapshot.docs.length;
          
          // Debug log
          if (querySnapshot.docs.length > 0) {
            console.log(`Employee ${employee.eName} ${employee.eSurname} has ${querySnapshot.docs.length} pending leave requests`);
          }
        }
      }
      
      console.log('Pending leave counts:', counts);
      setPendingLeaveCounts(counts);
    } catch (error) {
      console.error('Error fetching pending leave counts:', error);
    }
  };

  // Bekleyen izin taleplerini detaylı olarak getir
  const fetchPendingLeavesList = async () => {
    try {
      const q = query(
        collection(db, 'LeaveRequests'),
        where('status', '==', 'PENDING')
      );
      const querySnapshot = await getDocs(q);
      
      const pendingLeaves = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Çalışan bilgilerini bul
        const employee = employees.find(emp => emp.id === data.employeeId);
        return {
          id: doc.id,
          ...data,
          employeeName: employee ? `${employee.eName} ${employee.eSurname}` : 'Bilinmeyen Çalışan',
          employee: employee
        };
      });
      
      console.log('📊 Fetched pending leaves:', pendingLeaves.length);
      setPendingLeavesList(pendingLeaves);
      
      // Aynı zamanda pendingLeaveCounts'u da güncelle
      const counts = {};
      pendingLeaves.forEach(leave => {
        if (leave.employeeId) {
          counts[leave.employeeId] = (counts[leave.employeeId] || 0) + 1;
        }
      });
      setPendingLeaveCounts(counts);
      console.log('📊 Updated pending counts:', counts);
    } catch (error) {
      console.error('Error fetching pending leaves list:', error);
    }
  };

  // Bekleyen izin talepleri modalını aç
  const openPendingLeavesModal = async () => {
    await fetchPendingLeavesList();
    setShowPendingLeavesModal(true);
  };

  // İzin talebini sil
  const deleteLeaveRequest = async (requestId) => {
    try {
      const requestRef = doc(db, 'LeaveRequests', requestId);
      await deleteDoc(requestRef);
      
      toast.success('İzin talebi başarıyla silindi');
      
      // Listeyi yenile (bu aynı zamanda counts'u da güncelleyecek)
      await fetchPendingLeavesList();
    } catch (error) {
      console.error('Error deleting leave request:', error);
      toast.error('İzin talebi silinirken hata oluştu');
    }
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [employees, filteredEmployees]);

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Add this new function to handle column resizing
  const handleColumnResize = (e, columnKey) => {
    if (!resizingColumn) return;

    const table = tableRef.current;
    if (!table) return;

    const th = table.querySelector(`th[data-column="${columnKey}"]`);
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    
    // Set minimum width to prevent columns from becoming too narrow
    if (newWidth >= 100) {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    }
  };

  // Add these event handlers for mouse events
  const handleMouseDown = (e, columnKey) => {
    setResizingColumn(columnKey);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setResizingColumn(null);
  };

  const handleMouseMove = (e) => {
    if (resizingColumn) {
      handleColumnResize(e, resizingColumn);
    }
  };

  // Add effect to handle mouse events
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn]);

  // Effect to check if selected employee has a user account
  useEffect(() => {
    const checkUserExists = async () => {
      if (selectedEmployee && selectedEmployee.eMail) {
        setIsCheckingUser(true);
        setSelectedEmployeeHasUser(false); // Reset
        try {
          // Check Users collection first based on email
          // IMPORTANT: Assumes the email field in Users collection is named 'email'
          const usersRef = collection(db, 'Users');
          const q = query(usersRef, where('email', '==', selectedEmployee.eMail));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            setSelectedEmployeeHasUser(true);
          } else {
             // Optional: Check Employees collection for a userId field if that exists
             // Example: if (selectedEmployee.userId) setSelectedEmployeeHasUser(true);
             // For now, we rely only on the Users collection check
             setSelectedEmployeeHasUser(false);
          }
        } catch (error) {
          console.error('Error checking for existing user:', error);
          toast.error('Kullanıcı durumu kontrol edilirken hata oluştu.');
          setSelectedEmployeeHasUser(false); // Assume no user on error
        } finally {
          setIsCheckingUser(false);
        }
      } else {
        setSelectedEmployeeHasUser(false); // No employee selected or no email
        setIsCheckingUser(false);
      }
    };

    checkUserExists();
  }, [selectedEmployee]); // Re-run when selectedEmployee changes

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Çalışan Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button 
                className={styles.notificationButton}
                onClick={openPendingLeavesModal}
                title={`Bekleyen İzin Talepleri: ${totalPendingLeaves}`}
              >
                <FaBell className={styles.buttonIcon} />
                {totalPendingLeaves > 0 && (
                  <span className={styles.notificationBadge}>
                  </span>
                )}
              </button>
              <button className={styles.addButton} onClick={openAddModal}>
                <FaUserPlus className={styles.buttonIcon} /> Yeni Çalışan Ekle
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalEmployees}</p>
                  <h3 className={styles.cardTitle}>Toplam Çalışan</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Çalışan</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Çalışan</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="İsim, soyisim veya TC no ile ara (min. 3 karakter)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>
                  En az 3 karakter girin
                </span>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedEmployee && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedEmployee.eName} {selectedEmployee.eSurname}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedEmployee.Active === 1 
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedEmployee.Active === 1 ? 'Aktif' : 'İnaktif'}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button className={`${styles.actionButton} ${styles.cardButton}`} onClick={openCardModal}>
                <FaIdCard className={styles.actionIcon} />
                <span>Çalışan Kartı</span>
              </button>

              <button className={`${styles.actionButton} ${styles.editButton}`} onClick={openEditModal}>
                <FaUserEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.statusButton}`} onClick={handleStatusToggle}>
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Durum Değiştir</span>
              </button>
              
              {!userAuthorization?.includes('Kısıtlı Admin') && (
                <button className={`${styles.actionButton} ${styles.filesButton}`} onClick={openFileModal}>
                  <FaFolderOpen className={styles.actionIcon} />
                  <span>Dosyalar</span>
                </button>
              )}
              
              <button className={`${styles.actionButton} ${styles.projectsButton}`} onClick={openProjectsModal}>
                <FaProjectDiagram className={styles.actionIcon} />
                <span>Projeler</span>
              </button>

              <button
                className={`${styles.actionButton} ${styles.createUserButton}`}
                onClick={openCreateUserModal}
                disabled={isCheckingUser || selectedEmployeeHasUser}
                title={
                    isCheckingUser ? "Kullanıcı durumu kontrol ediliyor..." :
                    selectedEmployeeHasUser ? "Bu çalışan için zaten bir kullanıcı hesabı var" :
                    "Çalışan için kullanıcı hesabı oluştur"
                }
              >
                <FaUserCog className={styles.actionIcon} />
                <span>
                    {isCheckingUser ? "Kontrol..." : selectedEmployeeHasUser ? "Hesap Var" : "Hesap Oluştur"}
                </span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          {/* Pagination Controls - Hidden when filters are active */}
          {!isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  Sayfa {currentPage} / {totalPages} ({totalEmployees} toplam çalışan)
                </span>
                {isLoading && <span className={styles.loadingIndicator}> Yükleniyor...</span>}
              </div>
              <div className={styles.paginationControls}>
              <button
                className={styles.paginationButton}
                onClick={() => goToPage(1)}
                disabled={currentPage === 1 || isLoading}
              >
                İlk
              </button>
              <button
                className={styles.paginationButton}
                onClick={goToPreviousPage}
                disabled={currentPage === 1 || isLoading}
              >
                <FaChevronLeft /> Önceki
              </button>
              <span className={styles.pageNumbers}>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`${styles.pageNumber} ${currentPage === pageNum ? styles.activePage : ''}`}
                      onClick={() => goToPage(pageNum)}
                      disabled={isLoading}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={currentPage === totalPages || isLoading}
              >
                Sonraki <FaChevronRight />
              </button>
              <button
                className={styles.paginationButton}
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages || isLoading}
              >
                Son
              </button>
            </div>
          </div>
          )}

          {/* Show filter info when filters are active */}
          {isFilterActive && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  {filteredEmployees.length} sonuç bulundu (toplam {totalEmployees} çalışan içinden)
                </span>
              </div>
            </div>
          )}

          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table 
                  ref={tableRef}
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        effectiveVisibleColumns[key] && (
                          <th 
                            key={key}
                            data-column={key}
                            className={`${resizingColumn === key ? styles.resizing : styles.resizable}`}
                            style={{ width: columnWidths[key] || 'auto' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                          >
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => (
                        !emp.isEmpty && (
                          <tr 
                            key={emp.id} 
                            data-employee-id={emp.id}
                            onClick={() => handleEmployeeSelect(emp)}
                            className={selectedEmployee && selectedEmployee.id === emp.id ? styles.selectedRow : ''}
                          >
                            {Object.keys(columns).map(key => 
                              effectiveVisibleColumns[key] && (
                                <td key={key} title={emp[key]}>
                                  {key === 'Active' ? (
                                    <span className={`${styles.statusBadge} ${
                                      emp.Active === undefined || emp.Active === null || isNaN(emp.Active)
                                        ? styles.statusNoData
                                        : emp.Active === 1
                                          ? styles.statusActive
                                          : styles.statusInactive
                                    }`}>
                                      {emp.Active === undefined || emp.Active === null || isNaN(emp.Active)
                                        ? '-'
                                        : emp.Active === 1
                                          ? 'aktif'
                                          : 'inaktif'}
                                    </span>
                                  ) : key === 'isHandicappedPerson' || key === 'isIntern' || key === 'isRetired' ? (
                                    truncateText(emp[key] ? 'Evet' : 'Hayır')
                                  ) : key === 'nationality' ? (
                                    truncateText(emp[key] === 173 ? 'Turkey' : emp[key])
                                  ) : key === 'employeeExitDate' ? (
                                    formatDate(emp[key])
                                  ) : (
                                    truncateText(emp[key])
                                  )}
                                </td>
                              )
                            )}
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(effectiveVisibleColumns).filter(key => effectiveVisibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun çalışan bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {showCardModal && selectedEmployee && (
        <EmployeeCardModal
          employee={selectedEmployee}
          onClose={() => setShowCardModal(false)}
          onEdit={() => {
            setShowCardModal(false);
            setShowEditModal(true);
          }}
        />
      )}

      {showEditModal && selectedEmployee && (
        <EditEmployeeModal
          selectedEmployee={selectedEmployee}
          onClose={() => setShowEditModal(false)}
          onUpdate={refreshEmployees}
        />
      )}
      {showFileModal && selectedEmployee && (
        <FileUploadModal
          employee={selectedEmployee}
          onClose={(updatedEmployee, keepOpen) => {
            if (updatedEmployee) {
              // Update the selected employee with the new data
              setSelectedEmployee(updatedEmployee);
              // Update the employee in the employees list
              setEmployees(prevEmployees => 
                prevEmployees.map(emp => 
                  emp.id === updatedEmployee.id ? updatedEmployee : emp
                )
              );
            } else {
              // If no updated employee data, refresh employees
              refreshEmployees();
            }
            
            // Only close the modal if keepOpen is not true
            if (!keepOpen) {
              setShowFileModal(false);
            }
          }}
        />
      )}
      {showProjectsModal && selectedEmployee && (
        <ProjectsModal
          employee={selectedEmployee}
          onClose={() => setShowProjectsModal(false)}
        />
      )}
      {showAddModal && (
        <AddEmployeeModal
          onClose={() => setShowAddModal(false)}
          onUpdate={refreshEmployees}
        />
      )}
      {showInsuranceModal && selectedEmployee && (
        <AddInsuranceModal
          show={showInsuranceModal}
          handleClose={() => setShowInsuranceModal(false)}
          employeeId={selectedEmployee.id}
          employeeName={`${selectedEmployee.eName} ${selectedEmployee.eSurname}`}
          employees={employees}
        />
      )}
      {showEditInsuranceModal && selectedInsuranceData && (
        <EditInsuranceModal
          show={showEditInsuranceModal}
          handleClose={() => {
            setShowEditInsuranceModal(false);
            setSelectedInsuranceData(null);
          }}
          selectedInsurance={selectedInsuranceData}
          onInsuranceUpdated={() => {
            setShowEditInsuranceModal(false);
            setSelectedInsuranceData(null);
            toast.success('Sigorta bilgileri güncellendi');
          }}
        />
      )}
      {showVideoModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.videoModal}>
            <div className={styles.videoContent}>
              <video controls autoPlay>
                <source src={currentVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button className={styles.closeButton} onClick={() => setShowVideoModal(false)}>
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      )}
      {showExitDateModal && selectedEmployee && (
        <ExitDateModal
          selectedEmployee={selectedEmployee}
          onClose={() => setShowExitDateModal(false)}
          onUpdate={refreshEmployees}
          onEmployeeUpdate={(updatedEmployee) => {
            // Dinamik olarak seçili çalışanı güncelle
            setSelectedEmployee(updatedEmployee);
            // Çalışanlar listesini de güncelle
            setEmployees(prevEmployees => 
              prevEmployees.map(emp => 
                emp.id === updatedEmployee.id ? updatedEmployee : emp
              )
            );
          }}
        />
      )}

      {showCreateUserModal && selectedEmployee && (
        <CreateUserModal
          employee={selectedEmployee}
          onClose={() => {
              setShowCreateUserModal(false);
              // Re-trigger the user check after modal closes by re-selecting
              if (selectedEmployee) {
                 // Create a shallow copy to ensure useEffect dependency changes
                handleEmployeeSelect({ ...selectedEmployee });
              }
          }}
          // onUserCreated could trigger a refresh or update state directly if needed
        />
      )}

      {/* Bekleyen İzin Talepleri Modal */}
      {showPendingLeavesModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.pendingLeavesModal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                🔔 Bekleyen İzin Talepleri ({totalPendingLeaves})
              </h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowPendingLeavesModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            <div className={styles.modalBody}>
              {pendingLeavesList.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>Bekleyen izin talebi bulunmamaktadır.</p>
                </div>
              ) : (
                <div className={styles.pendingLeavesList}>
                  {pendingLeavesList.map((leave) => (
                    <div 
                      key={leave.id} 
                      className={styles.pendingLeaveItem}
                      onClick={() => {
                        // O çalışanı seç ve odaklan
                        if (leave.employee) {
                          setSelectedEmployee(leave.employee);
                          setShowPendingLeavesModal(false);
                          
                          // Çalışan satırını bul ve odaklan
                          setTimeout(() => {
                            const employeeRow = document.querySelector(`tr[data-employee-id="${leave.employee.id}"]`);
                            if (employeeRow) {
                              employeeRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              employeeRow.style.backgroundColor = '#fef3c7';
                              setTimeout(() => {
                                employeeRow.style.backgroundColor = '';
                              }, 2000);
                            }
                          }, 100);
                        }
                      }}
                    >
                      <div className={styles.leaveItemHeader}>
                        <h4 className={styles.employeeName}>{leave.employeeName}</h4>
                        <span className={styles.leaveType}>
                          {leave.leaveType === 'yillik' ? 'Yıllık İzin' :
                           leave.leaveType === 'mazeret' ? 'Mazeret İzni' :
                           leave.leaveType === 'raporlu' ? 'Raporlu' :
                           leave.leaveType}
                        </span>
                      </div>
                      
                      <div className={styles.leaveItemDetails}>
                        <div className={styles.leaveDate}>
                          📅 {new Date(leave.startDate).toLocaleDateString('tr-TR')} - {new Date(leave.endDate).toLocaleDateString('tr-TR')}
                        </div>
                        <div className={styles.leaveDuration}>
                          ⏱️ {leave.leaveDays} gün
                        </div>
                        {leave.reason && (
                          <div className={styles.leaveReason}>
                            💬 {leave.reason}
                          </div>
                        )}
                      </div>
                      
                      <div className={styles.leaveItemFooter}>
                        <span className={styles.clickHint}>
                          👆 Tıklayarak çalışana odaklan
                        </span>
                        <button 
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Bu izin talebini silmek istediğinizden emin misiniz?')) {
                              deleteLeaveRequest(leave.id);
                            }
                          }}
                          title="İzin talebini sil"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className={styles.modalActions}>
              <button 
                className={`${styles.btn} ${styles.btnCancel}`}
                onClick={() => setShowPendingLeavesModal(false)}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default EmployeeManagementPage;